<script setup>
import { ref, watch, onMounted, onBeforeUnmount, inject, computed, markRaw, getCurrentInstance, nextTick } from 'vue';
import axios from 'axios';
import { useLevellingData } from '@/composables/useLevellingData';
import { useV3Drawer } from '@/composables/useV3Drawer';

import {
  LEVELLING_STATUS,
  LEVELLING_STATUS_ORDER,
  LEVELLING_METHOD,
  // Anchor roles (PD-1258): options for the drawer's Anchor facet.
  LEVELLING_ANCHOR_FILTER_OPTIONS,
  relativeTime,
  isRecent,
} from '@rm-ui/levelling/levellingMeta';
import { levellingRowActions, supportedRowActions } from '@rm-ui/levelling/levellingRowActions';
import { levellingFieldPrefs } from '@rm-ui/levelling/levellingFieldPrefs';
import { levellingSavedViews } from '@rm-ui/levelling/levellingSavedViews';
import { levellingList } from '@rm-ui/levelling/levellingList';
import { levellingAnchors } from '@rm-ui/levelling/levellingAnchors';
import { anchorSetupWizard } from '@rm-ui/levelling/anchorSetupWizard';

import utils from '@/common/utils.js';
import CcStatusPill from '@/components/levelling/controlCentre/CcStatusPill.vue';
import CcLevelChip from '@/components/levelling/controlCentre/CcLevelChip.vue';
import CcMethodChip from '@/components/levelling/controlCentre/CcMethodChip.vue';
import CcDocStatus from '@/components/levelling/controlCentre/CcDocStatus.vue';
import CcHeaderMenu from '@/components/levelling/controlCentre/CcHeaderMenu.vue';
import CcFieldsMenu from '@/components/levelling/controlCentre/CcFieldsMenu.vue';
import CcRowActions from '@/components/levelling/controlCentre/CcRowActions.vue';
import CcDrawerFilters from '@/components/levelling/controlCentre/CcDrawerFilters.vue';
import CcAnchorChip from '@/components/levelling/controlCentre/CcAnchorChip.vue';
import CcAnchorModal from '@/components/levelling/controlCentre/CcAnchorModal.vue';
import CcAnchorSetup from '@/components/levelling/controlCentre/CcAnchorSetup.vue';
import CcTour from '@/components/levelling/controlCentre/CcTour.vue';
import { levellingTour, LEVELLING_CC_TOUR } from '@rm-ui/levelling/levellingTour';

const props = defineProps({
  evaluation: { type: Boolean, default: false },
});

const emit = defineEmits(['openDocument', 'setLevel', 'editLevel', 'export', 'addDocument']);

const store = inject('store');
const loginState = inject('loginState');
const passedParams = inject('passedParams', null);
const instance = getCurrentInstance();

// One-shot read of any deep-link preset (e.g. a homepage Stats tile linking here
// with statuses/doc-types preselected). `.get()` returns and clears the params.
const presetFilters =
  passedParams && typeof passedParams.get === 'function' ? passedParams.get() : null;

// useLevellingData owns the doc-list FILTER STATE + options (search, doc type,
// hierarchy, classifiers, dates) and buildFilters(); the row DATA, sort, paging
// and lifecycle counts come from the dedicated Report/levellingList endpoint
// (levellingList), which sorts server-side on composite keys the doc index can't
// hold — level, evaluator, evaluation status, method.
const data = useLevellingData({ store, loginState, presetFilters });

const list = levellingList({
  http: axios,
  buildParams: () => data.buildFilters(true),
  pageSize: 25,
});

const userId = loginState?.user?.user_id || 'default';

// --- Anchor roles (PD-1258) ---
// Inert while the `levelling_anchor_roles` client setting is off: no column, no
// chip, no row action, no filter. `canGovern` additionally requires one of the
// roles listed in `levelling_anchor_roles_admin` (server re-checks every write).
// markRaw: this `self` object is passed as a prop to CcAnchorModal, and Vue 2.7's
// observer would otherwise unwrap its inner refs, making `.value` writes throw.
const anchors = markRaw(levellingAnchors({
  http: axios,
  getUser: () => loginState?.user,
  onToast: (msg) => console.warn(msg),
}));
// Anchors-only filter chip. Not persisted with the other facets: it's a
// transient "show me just these" lens, and it also reveals the Anchor column
// while active, which shouldn't silently survive a reload.
const anchorFilter = ref(false);
// Role whose anchor state is being changed — the modal is the ONLY confirm
// surface; the row action never toggles silently.
const anchorAsk = ref(null);
const anchorAskKind = ref('anchor');

/* Guided anchor setup (PD-1258). The banner and the wizard share ONE load: the
   register-wide data is the same for both, and the banner's coverage figure has to
   be the number the wizard then works from. Only fetched when the tenant has the
   wizard on — `anchorSetupData` self-skips otherwise. markRaw for the same reason
   as `anchors` above: it is passed as a prop. */
const setup = markRaw(anchorSetupWizard({ http: axios, anchors }));

/* Onboarding tour. markRaw'd like every other composable that crosses a prop boundary:
   Vue 2.7's observer would unwrap its inner refs and every `.value` write would throw.
   Keyed per user — a shared or demo machine should not suppress the next person's run. */
const tour = markRaw(levellingTour({
  steps: LEVELLING_CC_TOUR,
  storageKey: `lvl.ccTourDone:${userId}`,
}));
const setupOpen = ref(false);
/**
 * The page-header anchor action.
 *
 * Two destinations, decided by the shared model rather than here: when the set is
 * complete the useful place is the framework's coverage view — reopening a setup journey
 * with nothing left to do in it is a dead click. Otherwise it opens the wizard, in the
 * outstanding-evaluations state when there are drafts.
 */
function onAnchorHeadAction() {
  const st = setup.headState.value;
  if (st.to === 'framework') {
    const router = instance && instance.proxy && instance.proxy.$router;
    if (router) router.push({ name: 'levellingframework' }).catch(() => {});
    return;
  }
  openAnchorSetup(st.mode);
}

async function openAnchorSetup(mode) {
  setupOpen.value = true;
  // Load BEFORE choosing the state: `start()` decides from the register, so it has to
  // see the roles. Already-loaded reopens skip the fetch and decide immediately.
  if (!setup.roles.value.length) await setup.load();
  setup.start(mode);
}
/** A pending anchor from the wizard: evaluate it, and it confirms on save. */
function evaluatePendingAnchor(role) {
  setupOpen.value = false;
  const reference = role && (role.reference || role.system_number);
  if (reference) goEvaluate({ system_number: reference }, { anchor: true });
}

function rowIsAnchor(item) {
  return !!(item.levelling && item.levelling.is_anchor);
}
/** The server's persisted "at or above the CEO ceiling" review flag. */
function rowAboveCeiling(item) {
  return !!(item.levelling && item.levelling.above_ceiling);
}
function rowIsCeiling(item) {
  return !!(item.levelling && item.levelling.is_ceiling);
}
/* Picked by the setup wizard but not yet evaluated — a claim, not evidence. The
   chip renders it dashed and grey; without this it was indistinguishable from a
   confirmed anchor (beta already passed it, rm-portal did not). */
function rowIsPending(item) {
  return !!(item.levelling && item.levelling.is_pending);
}

function openAnchorModal(item, kind) {
  anchorAskKind.value = kind || 'anchor';
  anchorAsk.value = item;
}
async function onAnchorSaved() {
  anchorAsk.value = null;
  // The row's is_anchor comes from the list endpoint, so re-fetch to repaint the
  // column/filter rather than patching the row in place.
  await list.fetch();
}

// --- Component-local UI state ---
const searchOpen = ref(false);
const scrolledX = ref(false);
const shadowX = ref(0);
const scrollEl = ref(null);

// Composite fields resolved server-side, carried on each row's `levelling` object.
function rowStatus(item) {
  return (item.levelling && item.levelling.status_key) || 'not-started';
}
function rowMethod(item) {
  return (item.levelling && item.levelling.method) || null;
}
function rowLevelCode(item) {
  // Trust ONLY the endpoint's resolved level. Do NOT fall back to the raw
  // tpa_job_level field — that re-introduces stale/invalid prepopulated values
  // (e.g. "Professional 5") that the endpoint deliberately suppresses.
  return (item.levelling && item.levelling.level_code) || null;
}
function rowEvaluator(item) {
  return (item.levelling && item.levelling.evaluator_name) || data.evaluatorValueForItem(item) || '';
}
function rowUpdated(item) {
  return item.levelling ? item.levelling.evaluation_updated_date : null;
}

// Person-column display (evaluator + any isPersonColumn field): avatar + name.
// Names that identify the logged-in user (full name, first+last, username).
const currentUserNames = computed(() => {
  const u = loginState && loginState.user;
  if (!u) return [];
  const combos = [u.name, `${u.first_name || ''} ${u.last_name || ''}`, u.username]
    .map(s => (s || '').trim().toLowerCase())
    .filter(Boolean);
  return [...new Set(combos)];
});
function isCurrentUser(name) {
  const n = (name || '').trim().toLowerCase();
  return !!n && currentUserNames.value.includes(n);
}
function personName(item, f) {
  const raw = utils.removeTags(String((f.type === 'evaluator' ? rowEvaluator(item) : item[f.value]) ?? ''));
  // Show "You" when the person is the logged-in user (prototype).
  return isCurrentUser(raw) ? 'You' : raw;
}
function personInitials(name) {
  return (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => (w[0] ? w[0].toUpperCase() : '')).join('');
}
// Deterministic pastel from the name so each person keeps a stable avatar hue.
function personHue(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}

// Evaluation status (multi) + method (single) facets — pushed to the endpoint so
// they filter ACROSS the whole catalogue, not just the loaded page.
const statusFilter = ref([]);
const methodFilter = ref('all');
// Suppresses the reload-on-change watchers while we hydrate persisted filters at
// init (each list setter fetches; we want a single fetch after restore).
let restoringFilters = false;
watch(statusFilter, (v) => { if (!restoringFilters) list.setStatusFilter(Array.isArray(v) ? v : []); }, { deep: true });
watch(methodFilter, (v) => { if (!restoringFilters) list.setMethodFilter(v && v !== 'all' ? [v] : []); });

// --- Current-filter persistence (all drawer fields persist between sessions).
// The data composable already persists search + hierarchy + doc status/type;
// this covers the levelling-specific facets it doesn't own: evaluation status,
// method, evaluator and the updated-between range. localStorage, keyed per user.
const CURRENT_FILTERS_KEY = `levelling-cc-filters-v1:${userId}`;
function persistCurrentFilters() {
  try {
    localStorage.setItem(CURRENT_FILTERS_KEY, JSON.stringify({
      statusFilter: statusFilter.value,
      methodFilter: methodFilter.value,
      evaluatorFilter: list.evaluatorIncluded.value,
      anchorFilter: list.anchorIncluded.value,
      updatedFrom: list.updatedFrom.value,
      updatedTo: list.updatedTo.value,
    }));
  } catch (err) { /* storage unavailable */ }
}
function restoreCurrentFilters() {
  let raw = null;
  try { raw = localStorage.getItem(CURRENT_FILTERS_KEY); } catch (err) { /* unavailable */ }
  if (!raw) return;
  try {
    const f = JSON.parse(raw);
    restoringFilters = true;
    if (Array.isArray(f.statusFilter)) {
      statusFilter.value = f.statusFilter;
      list.statusIncluded.value = f.statusFilter;
    }
    if (f.methodFilter) {
      methodFilter.value = f.methodFilter;
      list.methodIncluded.value = f.methodFilter !== 'all' ? [f.methodFilter] : [];
    }
    if (Array.isArray(f.evaluatorFilter)) list.evaluatorIncluded.value = f.evaluatorFilter;
    if (Array.isArray(f.anchorFilter)) list.anchorIncluded.value = f.anchorFilter;
    list.updatedFrom.value = f.updatedFrom || null;
    list.updatedTo.value = f.updatedTo || null;
  } catch (err) { /* ignore malformed */ } finally {
    restoringFilters = false;
  }
}

// --- Field model ---
// Admin base default (PD-1120), in order: Level · Evaluation status · Method ·
// Job title · Job family · Evaluator · Updated. "Job family" is a client-specific
// hierarchy column (dynamic key), so it's injected after Job title from the
// most-specific family level; the broader "Family group" (parent) stays
// available-but-off, listed directly above it in Manage fields (see the pinBefore
// chain in fieldDefs). Reference / Family group / Document status default off.
// Per-tenant admin default, held in `client_setting` ('levelling_cc_defaults',
// a JSON array of visible field keys in order). Absent → the code default below.
const SETTING_DEFAULT_COLUMNS = 'levelling_cc_defaults';
const tenantDefaultColumns = computed(() => {
  const s = (data.config.value?.settings || []).find(x => x.setting === SETTING_DEFAULT_COLUMNS);
  if (!s || !s.value) return null;
  try {
    const parsed = JSON.parse(s.value);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch {
    return null;
  }
});

const codeBaseVisible = computed(() => {
  /* Anchor is added in `baseVisible` below, not here — see the note there. */
  const base = ['level', 'status', 'method', 'jobTitle', 'evaluator', 'updated'];
  const cols = data.hierarchyLevelColumns.value || [];
  if (cols.length) {
    const minPos = Math.min(...cols.map((c) => c.position));
    const fam = cols.filter((c) => c.position === minPos);
    if (fam.length) {
      // Most-specific level (lowest `level` number) = "Job family".
      const child = fam.reduce((a, b) => (b.level < a.level ? b : a));
      const i = base.indexOf('jobTitle');
      base.splice(i + 1, 0, child.field);
    }
  }
  return base;
});

// The admin/tenant default takes precedence; otherwise the code default.
const baseVisible = computed(() => {
  const base = [...(tenantDefaultColumns.value || codeBaseVisible.value)];
  /* Anchor is visible by default, and that has to be applied HERE rather than in
     codeBaseVisible: a tenant that has configured levelling_cc_defaults bypasses the
     code default entirely, and that setting predates this column so it can never
     mention it. Left in codeBaseVisible only, the column stayed hidden for exactly
     the clients that have anchors switched on — and `hiddenKeys` hides anything not
     in the base set. Positioned after Evaluator. */
  if (anchors.enabled.value && !base.includes('anchor')) {
    const i = base.indexOf('evaluator');
    base.splice(i >= 0 ? i + 1 : base.length, 0, 'anchor');
  }
  return base;
});

const fieldDefs = computed(() => {
  // The document-status column's label follows the client's configured doc-list
  // column text (as the legacy view did), falling back to "Document Status".
  const docListCols = data.config.value?.docListColumns || [];
  const docStatusText = (docListCols.find(c => c.value === 'doc_status_text') || {}).text || 'Document Status';
  const defs = [
    { key: 'level', label: 'Level', value: 'level', type: 'level', width: 110, minWidth: 92, sortable: true },
    { key: 'status', label: 'Evaluation Status', type: 'status', width: 185, minWidth: 170, locked: true },
    { key: 'method', label: 'Method', type: 'method', width: 130, minWidth: 118 },
    { key: 'jobTitle', label: 'Job Title', value: 'doc_name', type: 'text', grow: true, minWidth: 200, locked: true, sortable: true },
    { key: 'ref', label: 'Reference', value: 'system_number', type: 'text', width: 135, minWidth: 118, sortable: true },
    { key: 'docType', label: 'Doc Type', value: 'doc_type', type: 'text', width: 150, minWidth: 130, sortable: true },
    { key: 'docStatus', label: docStatusText, value: 'doc_status_text', type: 'docStatus', width: 125, minWidth: 108, sortable: true },
    { key: 'evaluator', label: 'Evaluator', value: data.evaluatorColumnField.value, type: 'evaluator', width: 165, minWidth: 150, isPersonColumn: true, sortable: true },
    /* Anchor roles (PD-1258): only exists while the feature is on, and sits directly
       after Evaluator. Visible by default — see baseVisible. */
    ...(anchors.enabled.value
      /* pinAfter: the position is part of the design, not a user preference — see
         safeOrder. Without it a stored column order kept the column wherever it sat
         when the user's prefs were first saved. */
      ? [{ key: 'anchor', label: 'Anchor', type: 'anchor', width: 96, minWidth: 88, pinAfter: 'evaluator' }]
      : []),
    { key: 'updated', label: 'Updated', type: 'updated', width: 130, minWidth: 116, sortable: true },
    /* The ceiling review flag stays after Updated (it is a much rarer signal than the
       anchor mark, so it does not earn a place among the columns you always read). */
    ...(anchors.enabled.value
      ? [{ key: 'aboveCeiling', label: 'Review', type: 'aboveCeiling', width: 104, minWidth: 92 }]
      : []),
  ];
  /* Hierarchy levels are a CHAIN and always read broad → specific (Job family group
     before Job family). Only the most specific level is on by default (codeBaseVisible),
     so without a pin its parents land wherever the leftover hidden fields go — below
     Review and the classifiers in Manage fields, i.e. after the child they contain.
     The chain's internal order belongs to the hierarchy, not to the user, so each level
     is pinned before the next level down; the most specific one stays freely movable
     and its parents follow it. See pinBefore in levellingFieldPrefs. */
  const hryCols = data.hierarchyLevelColumns.value || [];
  hryCols.forEach((hc, i) => {
    // Levels of one hierarchy are contiguous and broad-first, so the next entry at the
    // same position IS the level immediately below this one.
    const below = hryCols.find((c, ci) => ci > i && c.position === hc.position);
    defs.push({
      key: hc.field, label: hc.label, value: hc.field, type: 'text',
      width: 150, minWidth: 130, sortable: true,
      ...(below ? { pinBefore: below.field } : {}),
    });
  });
  (data.classifierTypes.value || []).forEach(ct => {
    defs.push({ key: 'classifier_' + ct.ct_id, label: ct.ct_name, value: 'classifier_' + ct.ct_id, type: 'text', width: 150, minWidth: 130, sortable: true });
  });
  const existing = new Set(defs.map(d => d.value).filter(Boolean));
  (data.config.value?.docListColumns || []).forEach(c => {
    if (existing.has(c.value)) return;
    existing.add(c.value);
    defs.push({ key: c.value, label: c.text, value: c.value, type: 'text', width: 150, minWidth: 130, sortable: !c.isPersonColumn, isDate: c.isDate, isPersonColumn: c.isPersonColumn });
  });
  return defs;
});

// User field prefs persist SERVER-SIDE per user (PD-1120: not localStorage) via
// the shared user_view_setting table; localStorage stays an instant cache.
const FIELD_PREFS_KEY = 'levelling-cc-prefs';
const prefs = levellingFieldPrefs({
  storageKey: `levelling-cc-prefs-v2:${userId}`,
  getFieldDefs: () => fieldDefs.value,
  getBaseVisible: () => baseVisible.value,
  load: async () => {
    const v = await loginState.getUserViewSetting(FIELD_PREFS_KEY);
    return v && typeof v === 'object' ? v : null;
  },
  save: (blob) => loginState.saveUserViewSetting(FIELD_PREFS_KEY, blob),
  /* Bump when a column's default visibility changes. Rev 1: the Anchor column moved
     from off-by-default to on, and existing users had the old default frozen into
     their saved prefs (see _reconcileDefaults). Listed unconditionally — if the
     feature is off the key isn't a known field, so stripping it is a no-op. */
  defaultsRev: 1,
  getForceVisible: () => ['anchor'],
});

/* Either anchors filter: the toolbar chip, or the drawer facet narrowed to anchors
   ('anchor' / 'ceiling' — 'none' is the opposite, so it must NOT reveal). */
const anchorFilterActive = computed(() => {
  if (anchorFilter.value) return true;
  const inc = list.anchorIncluded.value;
  return Array.isArray(inc) && inc.some((v) => v === 'anchor' || v === 'ceiling');
});

/* Visible columns, with one override: while an anchors filter is active the Anchor
   column is revealed even if the user has it hidden in Fields (PD-1258) — the mark has
   to be visible on the rows it just filtered to. Rebuilt from safeOrder so the column
   lands in its user-chosen position, not appended.

   BOTH filters count: the toolbar chip and the drawer's Anchor facet. Only the chip
   used to reveal it, so filtering via the drawer isolated anchors while hiding the
   very column that says which they are. */
const visibleFields = computed(() => {
  if (!anchors.enabled.value || !anchorFilterActive.value) return prefs.visibleFields.value;
  const byKey = prefs.byKey.value;
  const off = new Set(prefs.hiddenKeys.value);
  off.delete('anchor');
  return prefs.safeOrder.value.map((k) => byKey[k]).filter((d) => d && (d.locked || !off.has(d.key)));
});

// Admin-only: publish the current visible column set/order as the tenant default
// (client_setting 'levelling_cc_defaults'). Reused by every user's Reset.
const canSetOrgDefault = computed(() => (loginState?.permittedRoutes || []).includes('admin'));
async function saveOrgDefaultColumns() {
  const cols = prefs.visibleFields.value.map(f => f.key);
  try {
    await axios.post('admin/settings', [ { setting: SETTING_DEFAULT_COLUMNS, value: cols } ]);
  } catch (err) {
    console.error('Could not save the organisation default columns:', err);
  }
}

// Grid template shared by the header and every row, so drag-resizing a column
// moves both in sync. The Actions column is a FIXED 175px track pinned to the
// end (see .cc-cell-actions grid-column). To keep it flush-right with no dead
// space after any column is resized to a fixed width, guarantee at least one
// flexible track: if no visible column is still growable, append a 1fr spacer.
const ACTIONS_COL_PX = 150;
const gridCols = computed(() => {
  const cols = visibleFields.value.map(f => {
    const w = prefs.colWidths.value[f.key];
    if (w) return w + 'px';
    if (f.grow) return `minmax(${f.minWidth || 200}px, 1.6fr)`;
    return (f.width || 140) + 'px';
  });
  const hasFlex = visibleFields.value.some(f => f.grow && !prefs.colWidths.value[f.key]);
  return cols.join(' ') + (hasFlex ? '' : ' minmax(0, 1fr)') + ` ${ACTIONS_COL_PX}px`;
});
const gridMinWidth = computed(() =>
  visibleFields.value.reduce((acc, f) => acc + (prefs.colWidths.value[f.key] || f.width || f.minWidth || 130), 0) + ACTIONS_COL_PX
);

function startResize(field, e) {
  e.preventDefault();
  e.stopPropagation();
  const cell = e.currentTarget.parentElement;
  const startW = cell.getBoundingClientRect().width;
  const startX = e.clientX;
  const move = (ev) => prefs.setWidth(field.key, startW + ev.clientX - startX);
  const up = () => {
    window.removeEventListener('mousemove', move);
    window.removeEventListener('mouseup', up);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  window.addEventListener('mousemove', move);
  window.addEventListener('mouseup', up);
}

// --- Sorting (server-side via the endpoint) ---
// Composite keys the doc index can't sort → resolved by the endpoint; every
// other field is a real index attribute the endpoint hands back to the index.
const COMPOSITE_SORT = new Set(['level', 'status', 'method', 'evaluator', 'updated', 'anchor']);
function sortKeyFor(field) {
  return COMPOSITE_SORT.has(field.key) ? field.key : field.value;
}
function setSort(field, dir) {
  list.setSort(sortKeyFor(field), dir);
}
function clearSort() {
  list.setSort('updated', 'desc');
}
const sortIsDefault = computed(() => list.sortKey.value === 'updated' && list.sortDir.value === 'desc');
function fieldSortValue(field) {
  return list.sortKey.value === sortKeyFor(field) ? list.sortDir.value : null;
}
function fieldSortActive(field) {
  return list.sortKey.value === sortKeyFor(field) && !sortIsDefault.value;
}

// Sort-menu labels follow the prototype's type-aware wording (LVL_FIELD_DEFS
// sortLabels): value columns read Highest/Lowest first, dates Newest/Oldest
// first, and everything textual A → Z / Z → A — never generic "Ascending".
const SORT_LABELS = {
  level: { asc: 'Lowest first', desc: 'Highest first' },
  updated: { asc: 'Oldest first', desc: 'Newest first' },
  // Reference codes are sequential numbers (PM-00153425) — magnitude wording,
  // not alphabet wording.
  ref: { asc: 'Lowest first', desc: 'Highest first' },
  anchor: { asc: 'Anchors last', desc: 'Anchors first' },
  _text: { asc: 'A → Z', desc: 'Z → A' },
};
function sortOptions(field) {
  const l = SORT_LABELS[field.key] || SORT_LABELS._text;
  return [{ value: 'asc', label: l.asc }, { value: 'desc', label: l.desc }];
}

// --- Paging (server-side) ---
const displayTotal = computed(() => list.totalCount.value);
const totalPages = computed(() => list.totalPages.value);
const pageItems = computed(() => list.rows.value);

function handleRowsPerPage(opt) {
  list.setPageSize(opt);
}
function goPage(page) {
  list.setPage(page);
  if (scrollEl.value) scrollEl.value.scrollTop = 0;
}

// --- Status / method filter options + counts (from the endpoint) ---
const statusCounts = computed(() => {
  const c = list.counts.value || {};
  return {
    'not-started': c.notStarted || 0,
    'invited': c.invited || 0,
    'in-progress': c.inProgress || 0,
    'review': c.review || 0,
    'sent-back': c.sentBack || 0,
    'levelled': c.levelled || 0,
  };
});

const statusOptions = computed(() =>
  LEVELLING_STATUS_ORDER
    // Hide lifecycle states the engine can't produce yet unless they actually occur.
    .filter(k => ['not-started', 'in-progress', 'levelled'].includes(k) || statusCounts.value[k] > 0)
    .map(k => ({ value: k, label: LEVELLING_STATUS[k].short, count: statusCounts.value[k] }))
);

const methodOptions = [
  { value: 'all', label: 'All methods' },
  { value: 'evaluate', label: LEVELLING_METHOD.evaluate.label },
  { value: 'classify', label: LEVELLING_METHOD.classify.label },
];

// Row-density options — a dropdown (prototype LvlChipMenu), not a toggle.
const densityOptions = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
];

// The header create/invite actions open the shared EvaluateNew dialog (mounted
// in App.vue, gated by this route's meta.showLevelCreate) via the `addDocument`
// event the router-view already forwards. It only makes sense when the client
// has a levelling-enabled document type to create — otherwise disable it.
const canCreate = computed(() =>
  ((data.levellingTemplates && data.levellingTemplates.value) || []).length > 0
);
// Hidden (not removed) until the evaluation flow is reworked so "Invite a
// colleague" creates the role AND opens straight on the invite-user screen.
// Flip to true to restore the header button.
const SHOW_INVITE_COLLEAGUE = false;
function startNewRole() {
  if (canCreate.value) emit('evaluateNewDocument');
}

function toggleStatusFilter(key) {
  statusFilter.value = statusFilter.value.includes(key)
    ? statusFilter.value.filter(k => k !== key)
    : [...statusFilter.value, key];
}

const docTypeHeaderOptions = computed(() =>
  (data.docTypeSummaryFiltered.value || []).map(t => ({ value: t.docType, label: t.docType }))
);
const docTypeSelected = computed(() => (data.docTypeIncludedLocal.value || []).map(t => t.docType));
function toggleDocTypeFilter(key) {
  const cur = docTypeSelected.value;
  const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
  data.setDocTypeKeys(next);
}

// --- KPI tiles (clickable — toggle the evaluation-status filter) ---
const kpis = computed(() => {
  const c = list.counts.value || {};
  return [
    { key: 'total', label: 'Number of roles', value: c.total, mdi: 'mdi-file-tree-outline', tone: 'neutral', status: null, hint: 'in this catalogue' },
    { key: 'levelled', label: 'Approved', value: c.levelled, mdi: 'mdi-check-decagram-outline', tone: 'green', status: 'levelled', hint: 'levels signed off' },
    { key: 'inProgress', label: 'In progress', value: c.inProgress, mdi: 'mdi-progress-clock', tone: 'accent', status: 'in-progress', hint: 'being evaluated now' },
    // status matches the statuses the COUNT includes (see getLevellingList:
    // toBeLevelled = notStarted + invited) so the tile number always equals
    // the filtered row count.
    { key: 'toBeLevelled', label: 'To be levelled', value: c.toBeLevelled, mdi: 'mdi-text-box-outline', tone: 'amber', status: ['not-started', 'invited'], hint: 'not yet started' },
  ];
});

// A tile's status can be a single key or a group of keys — toggle the group
// as one unit (all on → clear all; otherwise select all).
function kpiStatuses(kpi) {
  return Array.isArray(kpi.status) ? kpi.status : kpi.status ? [kpi.status] : [];
}
function kpiActive(kpi) {
  const keys = kpiStatuses(kpi);
  return keys.length > 0 && keys.every((k) => statusFilter.value.includes(k));
}
function onKpiClick(kpi) {
  const keys = kpiStatuses(kpi);
  if (!keys.length) { statusFilter.value = []; return; }
  statusFilter.value = kpiActive(kpi)
    ? statusFilter.value.filter((k) => !keys.includes(k))
    : [...new Set([...statusFilter.value, ...keys])];
}

// --- Row actions ---
// Acts this portal can handle today; invite / remind / review appear
// automatically once their lifecycle states + endpoints exist.
const SUPPORTED_ACTS = ['set-level', 'evaluate', 'reclassify', 'continue', 'edit', 'view-doc', 'outcome', 'review', 'anchor', 'anchor-evaluate', 'ceiling'];

function actionsFor(item) {
  return supportedRowActions(
    levellingRowActions(rowStatus(item), {
      method: rowMethod(item),
      isAnchor: rowIsAnchor(item),
      canAnchor: anchors.canGovern.value,
      isCeiling: rowIsCeiling(item),
      // Whether a ceiling exists anywhere decides if it can be offered here at all.
      hasCeiling: !!anchors.ceiling.value,
    }),
    SUPPORTED_ACTS
  );
}

// The route now renders this component directly (no vLevelling wrapper), so it
// owns navigation: openDocument is handled by App.vue's router-view; the level
// flows push to the evaluate route here.
function goEvaluate(item, opts) {
  const router = instance && instance.proxy && instance.proxy.$router;
  if (router && item && item.system_number) {
    // `anchor=1` pre-sets the anchor intent in the journey (PD-1258), which locks
    // it to a full evaluation — anchors can't be classified.
    const query = opts && opts.anchor ? { anchor: '1' } : undefined;
    router.push({ name: 'evaluate', params: { docReference: item.system_number }, query }).catch(() => {});
  }
}
// The read-only outcome summary IS the evaluate flow's summary step: a completed
// (and review-stage) evaluation resolves straight to it, read-only.
function goOutcome(item) {
  goEvaluate(item);
}
function onRowAct(act, item) {
  if (act === 'view-doc') return emit('openDocument', item);
  if (act === 'outcome') return goOutcome(item);
  // Anchor roles (PD-1258): the row menu never toggles state — it opens the
  // explainer/confirm modal, which owns the change.
  if (act === 'anchor') return openAnchorModal(item, 'anchor');
  // The CEO ceiling is the same confirm-through-a-modal contract as the anchor
  // mark, and now the ONLY route to it — the levelling journey deliberately doesn't
  // offer it (see LandingPage).
  if (act === 'ceiling') return openAnchorModal(item, 'ceo');
  // Anchoring a classified or not-yet-levelled role means running a full
  // evaluation with the anchor intent pre-set.
  if (act === 'anchor-evaluate') return goEvaluate(item, { anchor: true });
  // Review (awaiting approval / sent back): opens the completed outcome to
  // review — read-only summary until the sign-off screen exists, never the wizard.
  if (act === 'review') return goOutcome(item);
  return goEvaluate(item);
}
// Row click = the row's primary action, EXCEPT approved rows, which open the
// read-only outcome summary rather than the (editable) "Edit level" primary.
function onRowClick(item) {
  if (rowStatus(item) === 'levelled') return goOutcome(item);
  const primary = actionsFor(item).primary;
  if (primary) return onRowAct(primary.act, item);
  return emit('openDocument', item);
}

// --- Search (toolbar, debounced) ---
let searchTimer = null;
function onSearchInput(value) {
  data.filterText.value = value;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => list.reload(), 400);
}
function clearSearch() {
  data.filterText.value = '';
  searchOpen.value = false;
  list.reload();
}

// --- Filters / clear-all ---
const activeFilterCount = computed(() =>
  data.activeFilterCount.value +
  (statusFilter.value.length ? 1 : 0) +
  (methodFilter.value !== 'all' ? 1 : 0) +
  (list.evaluatorIncluded.value.length ? 1 : 0) +
  ((list.updatedFrom.value || list.updatedTo.value) ? 1 : 0) +
  // The chip lens and the drawer facet are separate filters — count both.
  (anchorFilter.value ? 1 : 0) +
  (list.anchorIncluded.value.length ? 1 : 0)
);

function clearAllFilters() {
  statusFilter.value = [];
  methodFilter.value = 'all';
  list.setEvaluatorFilter([]);
  list.setUpdatedRange(null, null);
  list.anchorIncluded.value = [];
  if (anchorFilter.value) {
    anchorFilter.value = false;
    list.anchorsOnly.value = false;
  }
  data.clearAllFilters();
}

// Anchors-only lens. Toggling it also reveals/hides the Anchor column, so the
// mark is visible on the rows it just filtered to.
function toggleAnchorFilter() {
  anchorFilter.value = !anchorFilter.value;
  list.setAnchorsOnly(anchorFilter.value);
}

// The drawer's Job family group / Job family filters mirror the prototype: the
// two levels of the PRIMARY job-architecture hierarchy (min position). Region /
// business-unit hierarchies are separate and out of scope here, so they're
// excluded — the drawer shows only the family axis.
const familyLevelColumns = computed(() => {
  const cols = data.hierarchyLevelColumns.value || [];
  if (!cols.length) return [];
  const minPos = Math.min(...cols.map(c => c.position));
  return cols.filter(c => c.position === minPos);
});

// --- Saved views ---
// Persisted server-side via the shared `user_view_setting` table (per user,
// view_name = 'levelling-cc-views'); localStorage stays as an instant cache and
// offline fallback. Same key as rm-portal-beta so views carry across portals.
const SAVED_VIEWS_KEY = 'levelling-cc-views';
const savedViews = levellingSavedViews({
  storageKey: `levelling-cc-views-v1:${userId}`,
  load: async () => {
    const v = await loginState.getUserViewSetting(SAVED_VIEWS_KEY);
    return Array.isArray(v) ? v : null;
  },
  save: (views) => loginState.saveUserViewSetting(SAVED_VIEWS_KEY, views),
  capture: () => ({
    filterText: data.filterText.value,
    hierarchyFieldFilters: { ...data.hierarchyFieldFilters.value },
    statusFilter: [...statusFilter.value],
    docTypeKeys: (data.docTypeIncludedLocal.value || []).map(t => t.docType),
    methodFilter: methodFilter.value,
    evaluatorFilter: [...list.evaluatorIncluded.value],
    anchorFilter: [...list.anchorIncluded.value],
    updatedFrom: list.updatedFrom.value,
    updatedTo: list.updatedTo.value,
  }),
  apply: (f) => {
    data.filterText.value = f.filterText || '';
    data.hierarchyFieldFilters.value = f.hierarchyFieldFilters || {};
    statusFilter.value = Array.isArray(f.statusFilter) ? f.statusFilter : [];
    if (Array.isArray(f.docTypeKeys)) data.setDocTypeKeys(f.docTypeKeys);
    methodFilter.value = f.methodFilter || 'all';
    // List-owned facets — set directly, the trailing reload() picks them up.
    list.evaluatorIncluded.value = Array.isArray(f.evaluatorFilter) ? f.evaluatorFilter : [];
    list.anchorIncluded.value = Array.isArray(f.anchorFilter) ? f.anchorFilter : [];
    list.updatedFrom.value = f.updatedFrom || null;
    list.updatedTo.value = f.updatedTo || null;
    list.reload();
  },
});

// --- Drawer registration -----------------------------------------------------
// The v3 chrome's ContextDrawer already renders the levelling ToC ("on this
// page" items from the router sub-nav), so this page contributes ONLY its
// filters to the drawer's context slot. Outside the v3 chrome the same filters
// render in-page instead (fallback aside).
const { setDrawerContext, clearDrawerContext } = useV3Drawer();
const isV3Chrome = computed(() => loginState?.user?.ui_layout === 'V2');

// Refs / composable objects — reactivity flows through the drawer's singleton
// slot. State writes stay here (vue/no-mutating-props), passed down as setters.
// markRaw is load-bearing: Vue 2.7's observer walks object props and REPLACES
// inner refs with their unwrapped values (defineReactive ref-unwrapping), which
// breaks every `data.x.value` access — including inside the composable itself.
const docTypeOptions = computed(() => (data.docTypeSummaryFiltered.value || []).map(t => t.docType));
const docTypeFilter = computed(() => (data.docTypeIncludedLocal.value || []).map(t => t.docType));

/* Anchor roles (PD-1258): the drawer facet's options. Empty while the feature is
   off, which is how CcDrawerFilters keeps "Anchor" out of "Add more fields". */
const anchorFilterOptions = computed(() =>
  anchors.enabled.value ? LEVELLING_ANCHOR_FILTER_OPTIONS : []
);

const drawerFilterProps = {
  data: markRaw(data),
  savedViews: markRaw(savedViews),
  statusFilter,
  statusOptions,
  docTypeOptions,
  docTypeFilter,
  familyColumns: familyLevelColumns,
  evaluatorOptions: list.evaluatorOptions,
  evaluatorFilter: list.evaluatorIncluded,
  anchorOptions: anchorFilterOptions,
  anchorFilter: list.anchorIncluded,
  updatedFrom: list.updatedFrom,
  updatedTo: list.updatedTo,
  activeFilterCount,
  onClearAll: clearAllFilters,
  onSetStatusFilter: (arr) => { statusFilter.value = Array.isArray(arr) ? arr : []; },
  onSetDocType: (arr) => data.setDocTypeKeys(Array.isArray(arr) ? arr : []),
  onSetEvaluator: (arr) => list.setEvaluatorFilter(Array.isArray(arr) ? arr : []),
  onSetAnchor: (arr) => list.setAnchorFilter(Array.isArray(arr) ? arr : []),
  onSetUpdated: (from, to) => list.setUpdatedRange(from || null, to || null),
};

// The v3 ContextDrawer stores props in a ref({}), whose observer unwraps the
// bare refs above into reactive plain VALUES before they reach the child. The
// non-v3 fallback below binds the drawer directly, so it must hand over the
// same plain-value shape (a computed keeps it reactive) — otherwise the child
// would receive raw refs and mis-render. data/savedViews stay markRaw objects.
const asideProps = computed(() => ({
  data: drawerFilterProps.data,
  savedViews: drawerFilterProps.savedViews,
  statusFilter: statusFilter.value,
  statusOptions: statusOptions.value,
  docTypeOptions: docTypeOptions.value,
  docTypeFilter: docTypeFilter.value,
  familyColumns: familyLevelColumns.value,
  evaluatorOptions: list.evaluatorOptions.value,
  evaluatorFilter: list.evaluatorIncluded.value,
  anchorOptions: anchorFilterOptions.value,
  anchorFilter: list.anchorIncluded.value,
  updatedFrom: list.updatedFrom.value,
  updatedTo: list.updatedTo.value,
  activeFilterCount: activeFilterCount.value,
  onClearAll: clearAllFilters,
  onSetStatusFilter: drawerFilterProps.onSetStatusFilter,
  onSetDocType: drawerFilterProps.onSetDocType,
  onSetEvaluator: drawerFilterProps.onSetEvaluator,
  onSetAnchor: drawerFilterProps.onSetAnchor,
  onSetUpdated: drawerFilterProps.onSetUpdated,
}));

function registerDrawer() {
  if (isV3Chrome.value) setDrawerContext(CcDrawerFilters, drawerFilterProps);
}
watch(isV3Chrome, registerDrawer);
onBeforeUnmount(() => clearDrawerContext());

// --- Misc ---
function sanitize(val) {
  return utils.sanitize(val);
}

// The frozen actions column's shadow fades in with the horizontal SCROLL
// POSITION: none at the far left, ramping to full over the first ~48px of
// scroll — so it appears progressively as content slides under the pinned
// column. `scrolledX` still tracks overflow existence (used for the left column).
function checkOverflowX(el) {
  if (!el) return;
  const over = el.scrollWidth - el.clientWidth > 1;
  if (scrolledX.value !== over) scrolledX.value = over;
  shadowX.value = over ? Math.min(1, el.scrollLeft / 48) : 0;
}
function onScrollX(e) {
  checkOverflowX(e.currentTarget);
}

// Big loader only for the very first load; subsequent fetches show the inline
// refresh spinner so the table doesn't blank out on every filter/sort/page.
const initialLoading = computed(() => data.isLoading.value || (list.loading.value && !list.rows.value.length));

// A signature of the doc-list filter state (NOT paging/sort/facets, which the
// list owns) — any change reloads the endpoint, debounced.
const filterSig = computed(() => JSON.stringify({
  q: data.filterText.value,
  hf: data.hierarchyFieldFilters.value,
  cf: data.classifierFieldFilters.value,
  ec: data.extraColumnFilters.value,
  dr: data.docCreatedDateRange.value,
  ds: (data.docStatusIncluded.value || []).map(s => s.status),
  dt: (data.docTypeIncludedLocal.value || []).map(t => t.docType),
  hff: data.hierarchyFilters.value,
  cff: data.classifierFilters.value,
}));

let initialised = false;
let sigTimer = null;
async function init() {
  if (initialised) return;
  initialised = true;
  // Loads filter options + applies presets/persisted filters (one doFilter).
  await data.fetchData();
  // Restore the levelling-specific facets before the first fetch so the initial
  // query reflects them; then persist on any subsequent change.
  restoreCurrentFilters();
  watch(
    [statusFilter, methodFilter, () => list.evaluatorIncluded.value, () => list.updatedFrom.value, () => list.updatedTo.value],
    () => persistCurrentFilters(),
    { deep: true },
  );
  // Reload the endpoint whenever the doc-list filter state changes (debounced).
  watch(filterSig, () => {
    if (sigTimer) clearTimeout(sigTimer);
    sigTimer = setTimeout(() => list.reload(), 300);
  });
  // Anchor roles (PD-1258): resolves the ceiling + the caller's authoritative
  // canGovern. Self-skips while the feature is off, and is deliberately not
  // awaited — the row marks come from the list endpoint, not from this.
  //
  // The setup data IS chained off it, though: the coverage banner can only render
  // once it knows the register, and `wizard` isn't known until this resolves. Left
  // unchained, the banner had nothing to show and the only way to load it was to
  // open the wizard — which is reached from the banner.
  anchors.load().then(() => {
    if (anchors.wizard.value) setup.load();
  });
  list.fetch();
}

watch(data.isLoading, (val) => { if (!val) init(); });

// Re-evaluate horizontal overflow (drives the frozen actions column's shadow)
// when the rendered rows, visible columns or column widths change.
watch(
  () => [list.rows.value.length, visibleFields.value.length, prefs.colWidths.value],
  () => nextTick(() => checkOverflowX(scrollEl.value)),
  { deep: true }
);

function onResize() { checkOverflowX(scrollEl.value); }

onMounted(() => {
  registerDrawer();
  if (!data.isLoading.value) init();
  nextTick(() => checkOverflowX(scrollEl.value));
  /* Auto-run once, and only after a paint — the tour measures real element rects, so
     starting it before the header and table exist would ring nothing and fall back to a
     centred card on every step. Skipping counts as done, so this fires once per browser
     until someone replays it from the header. */
  nextTick(() => { if (tour.shouldAutoRun()) tour.start(); });
  window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
});
</script>

<template>
  <div class="lvl-shell">
    <!-- Fallback filters aside — only outside the v3 chrome (no ContextDrawer). -->
    <aside v-if="!isV3Chrome" class="lvl-fallback-aside">
      <CcDrawerFilters v-bind="asideProps" />
    </aside>

    <!-- ===== Main ===== -->
    <div class="lvl-main">
      <!-- Page head (design v3-pagehead) -->
      <div class="lvl-pagehead" data-tour="pagehead">
        <span class="lvl-pagehead-ic"><v-icon size="16">mdi-view-dashboard-outline</v-icon></span>
        <h2>Levelling control centre</h2>
        <!-- Anchor state as ONE line beside the title, replacing the body banner
             (latest prototype): the banner cost ~120px of a viewport-bound workspace and
             its detail was reference material, not glanceable. Every string and branch
             lives in the shared `headState`. Hidden under 900px, as designed. -->
        <v-tooltip
          v-if="anchors.wizard.value && !setup.loading.value"
          bottom
          content-class="cc-tip"
        >
          <template v-slot:activator="{ on, attrs }">
            <span
              class="lvl-anchor-headnote"
              :class="{ 'is-done': setup.headState.value.tone === 'done',
                         'is-warn': setup.headState.value.tone === 'warn' }"
              v-bind="attrs"
              v-on="on"
              data-testid="lvl-anchor-headnote"
              data-tour="anchor"
            >
              <v-icon size="15">mdi-anchor</v-icon>{{ setup.headState.value.head }}
            </span>
          </template>
          <span>{{ setup.headState.value.tip }}</span>
        </v-tooltip>
        <span class="lvl-pagehead-spacer"></span>
        <!-- Replay the walkthrough. Sits OUTSIDE .lvl-pagehead-actions so the tour's
             "Start here" step can ring the action group without including this. -->
        <button
          type="button"
          class="lvl-pagehead-tut"
          title="Replay the guided walkthrough for this page"
          @click="tour.start()"
          data-testid="lvl-headbtn-tutorial"
        >
          <v-icon size="16">mdi-school-outline</v-icon> Run tutorial
        </button>
        <span class="lvl-pagehead-actions" data-tour="actions">
          <!-- Complete: the useful destination is the framework's coverage view, not a
               journey with nothing left to do in it. Otherwise it opens the wizard, in
               the outstanding-evaluations state when there are drafts. -->
          <span
            v-if="anchors.wizard.value && !setup.loading.value"
            class="lvl-anchor-cta-wrap"
            :title="setup.headState.value.ctaTip"
          >
            <v-btn
              small text
              class="lvl-headbtn lvl-headbtn--tonal"
              @click="onAnchorHeadAction"
              data-testid="lvl-anchor-headcta"
            >
              <v-icon size="15" left>{{ setup.headState.value.mdi }}</v-icon>
              {{ setup.headState.value.label }}
            </v-btn>
          </span>
          <v-btn
            v-if="SHOW_INVITE_COLLEAGUE"
            small outlined
            class="lvl-headbtn"
            :disabled="!canCreate"
            :title="canCreate ? 'Create a new role and invite a colleague to evaluate it' : 'No levelling-enabled document type is configured for this client'"
            @click="startNewRole"
            data-testid="lvl-headbtn-invite"
          >
            <v-icon size="16" left>mdi-account-plus-outline</v-icon>
            Invite a colleague
          </v-btn>
          <v-btn
            small depressed color="primary"
            class="lvl-headbtn"
            :disabled="!canCreate"
            :title="canCreate ? 'Create and level a new role' : 'No levelling-enabled document type is configured for this client'"
            @click="startNewRole"
            data-testid="lvl-headbtn-create"
          >
            <v-icon size="17" left>mdi-plus</v-icon>
            Level a new role
          </v-btn>
        </span>
      </div>

      <!-- Body: KPIs + toolbar stay fixed, only the table body scrolls. -->
      <div class="lvl-body">
        <div class="lvl-wrap">
          <!-- The anchor banner has moved into the page header (latest prototype): it
               cost ~120px of a viewport-bound workspace for detail that was reference
               material rather than glanceable. See .lvl-anchor-headnote above.
               NB: beta has since moved too, so CcAnchorBanner.vue is now referenced by
               neither portal — see the note in the shared model's banner block. -->

          <!-- KPI band: tiles toggle the evaluation-status filter -->
          <div class="lvl-stats" data-tour="stats">
            <button
              v-for="kpi in kpis"
              :key="kpi.key"
              class="lvl-stat"
              :class="[
                'lvl-stat--' + kpi.tone,
                { 'is-active': kpiActive(kpi), 'is-clickable': true },
              ]"
              :title="kpiStatuses(kpi).length ? 'Filter the table by this status' : 'Show all statuses'"
              @click="onKpiClick(kpi)"
              :data-testid="'lvl-stat-' + kpi.key"
            >
              <span class="lvl-stat-ic"><v-icon size="20">{{ kpi.mdi }}</v-icon></span>
              <span class="lvl-stat-txt">
                <span class="lvl-stat-value">{{ (kpi.value || 0).toLocaleString() }}</span>
                <span class="lvl-stat-label">{{ kpi.label }}</span>
                <span v-if="kpi.hint" class="lvl-stat-hint">{{ kpi.hint }}</span>
              </span>
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="initialLoading" class="lvl-loading">
            <v-progress-circular indeterminate size="50" color="grey"></v-progress-circular>
          </div>

          <!-- Roles panel -->
          <div v-else class="lvl-panel">
            <div class="cc-toolbar" data-tour="register">
              <h3 class="cc-title">Roles</h3>
              <span class="cc-count">{{ displayTotal.toLocaleString() }}</span>
              <span class="cc-tools">
                <span v-if="searchOpen || data.filterText.value" class="cc-search">
                  <v-icon size="16">mdi-magnify</v-icon>
                  <input
                    :value="data.filterText.value"
                    placeholder="Search title, reference or fields…"
                    aria-label="Search roles"
                    @input="onSearchInput($event.target.value)"
                    @keypress.enter="list.reload()"
                  />
                  <button class="cc-search-close" aria-label="Close search" @click="clearSearch">
                    <v-icon size="14">mdi-close</v-icon>
                  </button>
                </span>
                <button v-else class="cc-chipbtn" title="Search roles" @click="searchOpen = true">
                  <v-icon size="15">mdi-magnify</v-icon>
                  Search
                </button>
                <!-- Anchor roles (PD-1258): show only the register's calibration
                     references. Also reveals the Anchor column while active. -->
                <button
                  v-if="anchors.enabled.value"
                  class="cc-chipbtn lvl-anchor-filterchip"
                  :class="{ 'is-on': anchorFilter }"
                  :aria-pressed="String(anchorFilter)"
                  title="Show anchor roles only"
                  @click="toggleAnchorFilter"
                >
                  <v-icon size="15">mdi-anchor</v-icon>
                  Anchors only
                </button>
                <v-menu offset-y left content-class="cc-overlay cc-densitymenu" :close-on-content-click="true">
                  <template v-slot:activator="{ on, attrs }">
                    <button class="cc-chipbtn" title="Row density" v-bind="attrs" v-on="on">
                      <v-icon size="15">{{ prefs.density.value === 'compact' ? 'mdi-view-headline' : 'mdi-view-sequential-outline' }}</v-icon>
                      {{ prefs.density.value === 'compact' ? 'Compact' : 'Comfortable' }}
                      <v-icon size="15">mdi-chevron-down</v-icon>
                    </button>
                  </template>
                  <v-list dense>
                    <v-list-item
                      v-for="opt in densityOptions"
                      :key="opt.value"
                      @click="prefs.setDensity(opt.value)"
                    >
                      <v-list-item-icon class="cc-densitymenu-check">
                        <v-icon size="17" :style="{ opacity: prefs.density.value === opt.value ? 1 : 0 }">mdi-check</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>{{ opt.label }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <CcFieldsMenu
                  :order="prefs.safeOrder.value"
                  :hidden="prefs.hiddenKeys.value"
                  :defs="fieldDefs"
                  :canSetDefault="canSetOrgDefault"
                  @toggle="prefs.toggleField"
                  @move="prefs.moveField"
                  @reset="prefs.resetToDefault"
                  @save-default="saveOrgDefaultColumns"
                />
                <button v-if="activeFilterCount > 0" class="cc-chipbtn cc-chipbtn--clear" @click="clearAllFilters">
                  <v-icon size="15">mdi-close</v-icon>
                  Clear filters
                </button>
              </span>
            </div>

            <!-- Empty state -->
            <div v-if="displayTotal === 0 && !list.loading.value" class="cc-empty">
              <span class="cc-empty-ic"><v-icon size="24">mdi-magnify-remove-outline</v-icon></span>
              <h3>No roles match</h3>
              <p>Try a different search or status, or clear the filters to see all roles.</p>
              <v-btn small outlined @click="clearAllFilters">Clear filters</v-btn>
            </div>

            <!-- Table -->
            <div
              v-else
              ref="scrollEl"
              class="cc-scroll"
              :class="{ 'is-scrolled-x': scrolledX, 'is-compact': prefs.density.value === 'compact' }"
              :style="{ '--cc-actions-shadow': shadowX }"
              @scroll="onScrollX"
            >
              <div class="cc-grid" :style="{ minWidth: gridMinWidth + 'px' }">
                <!-- Header row -->
                <div class="cc-row cc-row--head" :style="{ gridTemplateColumns: gridCols }">
                  <span v-for="f in visibleFields" :key="'h_' + f.key" class="cc-colh">
                    <CcHeaderMenu
                      v-if="f.key === 'status'"
                      :label="f.label"
                      :options="statusOptions"
                      :value="statusFilter"
                      multi
                      :active="statusFilter.length > 0"
                      clear-label="Clear statuses"
                      title="Filter by status"
                      @pick="toggleStatusFilter"
                      @clear="statusFilter = []"
                    >
                      <template v-slot:option="{ option }">
                        <CcStatusPill :status="option.value" />
                      </template>
                    </CcHeaderMenu>
                    <CcHeaderMenu
                      v-else-if="f.key === 'docType' && docTypeHeaderOptions.length > 1"
                      :label="f.label"
                      :options="docTypeHeaderOptions"
                      :value="docTypeSelected"
                      multi
                      :active="!data.isDocTypeDefault.value"
                      clear-label="Reset to default"
                      title="Filter by document type"
                      @pick="toggleDocTypeFilter"
                      @clear="data.setDocTypeKeys([])"
                    />
                    <!-- Single doc type: no filter, and no sort (every row is the same value). -->
                    <span v-else-if="f.key === 'docType'" class="cc-colh-label">{{ f.label }}</span>
                    <CcHeaderMenu
                      v-else-if="f.key === 'method'"
                      :label="f.label"
                      :options="methodOptions"
                      :value="methodFilter"
                      :active="methodFilter !== 'all'"
                      clear-label="Clear method"
                      title="Filter by method"
                      @pick="methodFilter = $event"
                      @clear="methodFilter = 'all'"
                    />
                    <CcHeaderMenu
                      v-else-if="f.sortable"
                      :label="f.label"
                      :options="sortOptions(f)"
                      :value="fieldSortValue(f)"
                      :active="fieldSortActive(f)"
                      clear-label="Clear sort"
                      :title="'Sort by ' + f.label"
                      @pick="setSort(f, $event)"
                      @clear="clearSort"
                    />
                    <span v-else class="cc-colh-label">{{ f.label }}</span>
                    <span
                      class="cc-colresize"
                      title="Drag to resize · double-click to reset"
                      @mousedown="startResize(f, $event)"
                      @dblclick="prefs.resetWidth(f.key)"
                    ></span>
                  </span>
                  <span class="cc-colh cc-colh-actions">Actions</span>
                </div>

                <!-- Data rows -->
                <div
                  v-for="item in pageItems"
                  :key="item.doc_id"
                  class="cc-row cc-row--data"
                  :style="{ gridTemplateColumns: gridCols }"
                  role="button"
                  tabindex="0"
                  @click="onRowClick(item)"
                  :data-testid="'cc-row-' + item.system_number"
                >
                  <span v-for="f in visibleFields" :key="f.key" class="cc-cell" :class="'cc-cell--' + f.key">
                    <CcLevelChip
                      v-if="f.type === 'level'"
                      :code="rowLevelCode(item)"
                      :track-code="item.levelling ? item.levelling.track_code : ''"
                      :track-colour="item.levelling ? item.levelling.track_colour : ''"
                      :track-label="item.levelling ? item.levelling.track_label : ''"
                    />
                    <CcStatusPill v-else-if="f.type === 'status'" :status="rowStatus(item)" />
                    <CcMethodChip v-else-if="f.type === 'method'" :method="rowMethod(item)" />
                    <CcAnchorChip
                      v-else-if="f.type === 'anchor'"
                      :is-anchor="rowIsAnchor(item)"
                      :is-ceiling="rowIsCeiling(item)"
                      :is-pending="rowIsPending(item)"
                      :rationale="item.levelling ? item.levelling.anchor_rationale : null"
                    />
                    <!-- CEO ceiling review flag. Advisory: it says "look at this",
                         never "this is wrong" — the copy stays neutral about which
                         role is at fault, matching the summary banner. -->
                    <span
                      v-else-if="f.type === 'aboveCeiling'"
                      class="cc-ceilflag"
                      :class="{ 'is-on': rowAboveCeiling(item) }"
                      :title="rowAboveCeiling(item)
                        ? 'Flagged for review — this outcome is at or above the framework ceiling. Check this role and the ceiling role before sign-off; neither is assumed wrong.'
                        : undefined"
                    >
                      <template v-if="rowAboveCeiling(item)">
                        <v-icon size="15" color="warning">mdi-flag</v-icon> Review
                      </template>
                      <template v-else>&mdash;</template>
                    </span>
                    <CcDocStatus v-else-if="f.type === 'docStatus'" :value="(item.levelling && item.levelling.lifecycle_status_name) || item.doc_status_text" />
                    <span v-else-if="f.type === 'evaluator' || f.isPersonColumn" class="cc-person">
                      <template v-if="personName(item, f)">
                        <span
                          class="cc-avatar"
                          :style="{
                            background: 'hsl(' + personHue(personName(item, f)) + ' 62% 92%)',
                            color: 'hsl(' + personHue(personName(item, f)) + ' 45% 34%)',
                          }"
                        >{{ personInitials(personName(item, f)) }}</span>
                        <span class="cc-cell-txt cc-person-name">{{ personName(item, f) }}</span>
                      </template>
                      <span v-else class="cc-person-empty">—</span>
                    </span>
                    <span
                      v-else-if="f.type === 'updated'"
                      class="cc-updated"
                      :class="{ 'is-recent': isRecent(rowUpdated(item)) }"
                      :title="isRecent(rowUpdated(item)) ? 'Evaluated in the last 7 days' : ''"
                    >{{ relativeTime(rowUpdated(item)) }}</span>
                    <span v-else class="cc-cell-txt" v-html="sanitize(item[f.value])"></span>
                  </span>
                  <span class="cc-cell cc-cell-actions">
                    <CcRowActions :actions="actionsFor(item)" @act="onRowAct($event, item)" />
                  </span>
                </div>

                <div v-if="list.loading.value" class="cc-refreshing">
                  <v-progress-circular indeterminate size="20" width="2" color="grey"></v-progress-circular>
                </div>
              </div>
            </div>

            <!-- Pagination Footer — matches the prototype's lvl-cc-foot:
                 "Showing A–B of N" · Rows select · ‹ Page X of Y › -->
            <div class="cc-foot">
              <span class="cc-foot-showing">
                Showing
                <strong>{{ ((list.currentPage.value - 1) * list.rowsPerPage.value + 1).toLocaleString() }}–{{ Math.min(list.currentPage.value * list.rowsPerPage.value, displayTotal).toLocaleString() }}</strong>
                of {{ displayTotal.toLocaleString() }} {{ activeFilterCount > 0 ? 'matching roles' : 'roles' }}
              </span>
              <span class="cc-pager">
                <label class="cc-pgsize">
                  Rows
                  <select :value="list.rowsPerPage.value" @change="handleRowsPerPage(Number($event.target.value))">
                    <option v-for="opt in [25, 50, 100]" :key="opt" :value="opt">{{ opt }}</option>
                  </select>
                </label>
                <button
                  class="cc-pgbtn"
                  :disabled="list.currentPage.value <= 1"
                  aria-label="Previous page"
                  @click="goPage(list.currentPage.value - 1)"
                ><v-icon size="18">mdi-chevron-left</v-icon></button>
                <span class="cc-pgnum">Page {{ list.currentPage.value }} of {{ totalPages }}</span>
                <button
                  class="cc-pgbtn"
                  :disabled="list.currentPage.value >= totalPages"
                  aria-label="Next page"
                  @click="goPage(list.currentPage.value + 1)"
                ><v-icon size="18">mdi-chevron-right</v-icon></button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Anchor roles (PD-1258): the explainer + confirm surface for every anchor
         and CEO-ceiling change. Never a silent toggle. -->
    <!-- Guided anchor setup (PD-1258). Nothing is written until Confirm on its
         review step; closing re-fetches so the new marks paint. -->
    <CcTour :tour="tour" />

    <CcAnchorSetup
      v-if="setupOpen"
      :w="setup"
      :ceiling="anchors.ceiling.value"
      @close="setupOpen = false; list.fetch()"
      @evaluate="evaluatePendingAnchor"
    />

    <CcAnchorModal
      v-if="anchorAsk"
      :role="anchorAsk"
      :kind="anchorAskKind"
      :anchors="anchors"
      @close="anchorAsk = null"
      @saved="onAnchorSaved"
      @evaluate="goEvaluate(anchorAsk, { anchor: true })"
    />
  </div>
</template>

<style lang="scss" scoped>
/* ===== SHELL: fills the viewport under the app bar; the section nav and
   filters live in the chrome's ContextDrawer (or the fallback aside). ===== */
.lvl-shell {
  display: flex;
  width: 100%;
  height: calc(100vh - 50px);
  max-height: calc(100vh - 50px);
  overflow: hidden;
  background: var(--cc-canvas, #f6f7f9);
}

/* Fallback filters panel for the non-v3 layout (no ContextDrawer). */
.lvl-fallback-aside {
  flex: 0 0 auto;
  width: 280px;
  height: 100%;
  overflow-y: auto;
  background: var(--cc-surface, #ffffff);
  border-right: 1px solid var(--cc-border, #e5e7eb);
  padding: 10px 8px;
}

/* ===== MAIN ===== */
.lvl-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.lvl-pagehead {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 22px;
  min-height: 62px;
  box-sizing: border-box;
  background: var(--cc-surface, #ffffff);
  flex-shrink: 0;

  .lvl-pagehead-ic {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: var(--cc-accent-tint, #e9f0fd);

    .v-icon {
      color: var(--cc-accent, #1d5bd6) !important;
    }
  }

  // Prototype v3-pagehead h2 — compact sans, not serif.
  h2 {
    font-weight: 700;
    font-size: 14.5px;
    letter-spacing: -0.01em;
    color: var(--cc-text, #1f242d);
    margin: 0;
  }

  .lvl-pagehead-spacer {
    flex: 1;
  }

  .lvl-pagehead-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  /* Ported from the prototype's `.v3-app .rm-btn--sm` (v3.css): the header's actions
     are design-system small buttons — 8px radius, 12px inline padding, 12.5px/600 —
     not Vuetify's 28px/4px `small`, which read as a tier below the Run tutorial button
     sitting beside them. `.v-btn` is in the selector so the geometry beats Vuetify's
     own `.v-btn:not(.v-btn--round).v-size--small`, which is equally specific and would
     otherwise win or lose on stylesheet order.

     32px rather than the prototype's literal 30px: that height assumes the prototype's
     own line metrics, and 32 is both what rm-portal-beta ships for the same header and
     the closer match to Run tutorial under this app's 1.5 line-height. Both CTAs carry
     this class, so they stay the same size as each other. */
  .lvl-headbtn.v-btn {
    height: 32px;
    padding: 0 12px;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    box-shadow: none;
  }

  /* Vuetify pulls a leading icon 4px into the button's padding; the design has a plain
     8px gap inside a true 12px inset. */
  .lvl-headbtn .v-icon--left {
    margin-left: 0;
    margin-right: 8px;
  }

  /* The design system's `tonal` variant: a brand wash with AA-safe brand text and no
     border — `--action-tint` / `--action-text` in semantic.css (brand-50 / brand-700
     on light, 22% brand-500 / brand-300 on dark, which the color-mix below reproduces
     against our own accent). Geometry comes from `.lvl-headbtn` above, which both
     header CTAs share, so the two can't desync. */
  .lvl-headbtn--tonal {
    background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 10%, transparent) !important;
    color: var(--cc-accent, #1d5bd6) !important;
    border: 1px solid transparent !important;

    &::before { opacity: 0 !important; }
    &:hover { filter: brightness(0.97); }
    .v-icon { color: inherit; }
  }
}

.lvl-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-top: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-canvas, #EDF0F8);
}

/* The control centre is a viewport-bound data workspace: KPIs + toolbar stay
   fixed, only the table body scrolls. */
.lvl-wrap {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  // Prototype lvl-wrap: content capped and centred with breathing room either
  // side (like the homepage's central column), rather than full-bleed.
  width: 100%;
  max-width: 1600px;
  margin-inline: auto;
  padding: 20px 28px 16px;
}

/* ===== KPI TILES (design V3Stats band) ===== */
.lvl-stats {
  display: flex;
  gap: 14px;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.lvl-stat {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 170px;
  padding: 14px 16px;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 14px;
  text-align: left;
  cursor: default;

  &.is-clickable {
    cursor: pointer;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;

    &:hover {
      box-shadow: var(--cc-shadow, 0 4px 14px rgba(0, 0, 0, 0.07));
    }
  }

  &.is-active {
    border-color: var(--cc-accent, #1d5bd6);
    box-shadow: 0 0 0 1px var(--cc-accent, #1d5bd6) inset;
  }

  .lvl-stat-ic {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    flex-shrink: 0;
  }

  .lvl-stat-txt {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  // Prototype lvl-stat-v / lvl-stat-l / lvl-stat-s.
  .lvl-stat-value {
    font-size: 25px;
    font-weight: 700;
    line-height: 1.05;
    color: var(--cc-text, #1f242d);
  }

  .lvl-stat-label {
    font-size: 11.5px;
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--cc-text-2, #525c6b);
    margin-top: 3px;
    white-space: nowrap;
  }

  .lvl-stat-hint {
    font-size: 12px;
    color: var(--cc-text-3, #97a0af);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.lvl-stat--neutral .lvl-stat-ic { background: var(--cc-todo-bg, #f1f3f6); .v-icon { color: var(--cc-todo-fg, #5a6678) !important; } }
  &.lvl-stat--green .lvl-stat-ic { background: var(--cc-ok-bg, #e8f6ee); .v-icon { color: var(--cc-ok-fg, #1f7a53) !important; } }
  &.lvl-stat--accent .lvl-stat-ic { background: var(--cc-accent-tint, #e9f0fd); .v-icon { color: var(--cc-accent, #1d5bd6) !important; } }
  &.lvl-stat--amber .lvl-stat-ic { background: var(--cc-warn-bg, #fbf4e0); .v-icon { color: var(--cc-warn-fg, #8a6a00) !important; } }
}

/* ===== ROLES PANEL ===== */
.lvl-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--cc-surface, #ffffff);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 14px;
  padding: 14px 16px 0;
  overflow: hidden;
}

.lvl-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
}

/* ===== TOOLBAR ===== */
.cc-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0 12px;
  flex-shrink: 0;

  .cc-title {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin: 0;
  }

  // Prototype v3-count — small tabular pill.
  .cc-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    padding: 1px 8px;
    border-radius: 999px;
    background: var(--cc-surface-2, #f7f9fb);
    border: 1px solid var(--cc-border-soft, #edf0f7);
    color: var(--cc-text-2, #525c6b);
    font-size: 11px;
    font-weight: 650;
    font-variant-numeric: tabular-nums;
  }

  .cc-tools {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }
}

// Prototype v3-chipbtn — pill, 600/11.5.
.cc-chipbtn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border: 1px solid var(--cc-border, #e0e0e0);
  border-radius: 999px;
  background: var(--cc-surface, #ffffff);
  font-size: 11.5px;
  font-weight: 600;
  color: var(--cc-text-2, #525c6b);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--cc-surface-2, #f7f9fb);
    color: var(--cc-text, #1f242d);
  }

  &.cc-chipbtn--clear {
    color: var(--cc-danger, #b3261e);
  }

  /* Anchor roles (PD-1258): the active "Anchors only" lens. */
  &.lvl-anchor-filterchip.is-on {
    background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 12%, transparent);
    border-color: color-mix(in srgb, var(--cc-accent, #1d5bd6) 45%, transparent);
    color: var(--cc-accent, #1d5bd6);
  }
}

/* The Anchor column is a narrow mark column — centre it like the other chips. */
.cc-cell--anchor {
  justify-content: flex-start;
}

.cc-search {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--cc-accent, #1d5bd6);
  border-radius: 8px;
  background: var(--cc-surface, #ffffff);
  padding: 5px 8px;
  min-width: 280px;

  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 13.5px;
    color: var(--cc-text, #333333);
    background: transparent;

    &::placeholder {
      color: var(--cc-text-3, #aaaaaa);
    }
  }

  .cc-search-close {
    display: inline-flex;
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--cc-text-3, #999999);

    &:hover {
      color: var(--cc-text, #333333);
    }
  }
}

/* ===== TABLE ===== */
.cc-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid var(--cc-border, #eeeeee);
  border-radius: 8px;

  /* Slim scrollbars (design). */
  scrollbar-width: thin;
  scrollbar-color: var(--cc-scrollbar, rgba(16, 24, 40, 0.2)) transparent;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--cc-scrollbar, rgba(16, 24, 40, 0.2));
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;

    &:hover {
      background: var(--cc-scrollbar-hover, rgba(16, 24, 40, 0.34));
      background-clip: content-box;
    }
  }
  &::-webkit-scrollbar-corner {
    background: transparent;
  }

  &.is-scrolled-x .cc-row > :first-child {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: -1px;
      bottom: 0;
      width: 1px;
      box-shadow: 2px 0 6px rgba(0, 0, 0, 0.12);
    }
  }
}

.cc-grid {
  display: flex;
  flex-direction: column;
}

.cc-row {
  display: grid;
  align-items: center;
}

.cc-row--head {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--cc-surface-2, #f7f9fb);
  border-bottom: 2px solid var(--cc-border, #eeeeee);

  .cc-colh {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 11px 14px;
    min-width: 0;
    position: relative;

    // Prototype plain header spans — 10.5px/700/upper/.05em/text-3.
    .cc-colh-label {
      font-weight: 700;
      font-size: 10.5px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--cc-text-3, #97a0af);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  // Prototype v3-ttbl-act (head) — 11.5px/650/text-2, not uppercase.
  // Always the LAST grid track (past the flex spacer) and frozen to the right
  // edge (corner cell: sticky top + right).
  .cc-colh-actions {
    grid-column: -2 / -1;
    position: sticky;
    right: 0;
    z-index: 4;
    background: var(--cc-surface-2, #f7f9fb);
    border-left: 1px solid var(--cc-border, #e5e7eb);
    font-weight: 650;
    font-size: 11.5px;
    color: var(--cc-text-2, #525c6b);
  }

  .cc-colresize {
    position: absolute;
    top: 0;
    right: -4px;
    width: 8px;
    height: 100%;
    cursor: col-resize;
    z-index: 3;

    &:hover {
      background: var(--cc-accent-soft, rgba(29, 91, 214, 0.15));
    }
  }
}

.cc-row--data {
  border-bottom: 1px solid var(--cc-border-soft, #f0f0f0);
  cursor: pointer;

  &:hover {
    background: var(--cc-surface-2, #f9fafb);
  }

  // Prototype lvl-cc-cell: flex + align-items center so chips, the No-level pill
  // and text all sit on one centre line regardless of their own heights. 13px
  // vertical padding, roomier columns; generic cell text 13px/text-2.
  .cc-cell {
    display: flex;
    align-items: center;
    padding: 13px 14px;
    min-width: 0;
    overflow: hidden;
    font-size: 13px;
    color: var(--cc-text-2, #525c6b);
  }

  // Job title — the prominent column (v3-ttbl-doc .t): 14px/650, primary text.
  .cc-cell--jobTitle {
    font-size: 14px;
    font-weight: 650;
    letter-spacing: -0.01em;
    color: var(--cc-text, #1f242d);
  }

  // Reference — quiet, tabular (v3-ttbl-ref).
  .cc-cell--ref {
    font-size: 12px;
    color: var(--cc-text-3, #97a0af);
    font-variant-numeric: tabular-nums;
  }

  // Evaluator name (v3-ttbl-owner .nm).
  .cc-cell--evaluator {
    font-size: 12.5px;
  }

  // Person columns (evaluator + isPersonColumn): initials avatar + name.
  /* CEO ceiling review flag. Warn-toned, not danger: the outcome is valid and
     saved, it just wants a second look before sign-off. An em-dash when clear, so
     the column reads as a column rather than a scatter of icons. */
  .cc-ceilflag {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 12px; font-weight: 700; color: var(--v3-text-3);
  }
  .cc-ceilflag.is-on { color: var(--v3-warn); }

  .cc-person {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    .cc-avatar {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 999px;
      font-size: 9.5px;
      font-weight: 700;
      letter-spacing: 0.02em;
      user-select: none;
    }

    .cc-person-name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .cc-person-empty {
      color: var(--cc-text-3, #97a0af);
    }
  }

  // Actions float to the right edge of the table (prototype .lvl-cc .v3-ttbl-act:
  // sticky right, opaque, with a left divider) so they stay reachable while the
  // rest of the columns scroll under them. Pinned to the LAST track (past the
  // flex spacer) so it stays a fixed width, flush right, regardless of resizing.
  .cc-cell-actions {
    grid-column: -2 / -1;
    position: sticky;
    right: 0;
    z-index: 1;
    overflow: visible;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: var(--cc-surface, #ffffff);
    border-left: 1px solid var(--cc-border, #e5e7eb);
  }

  &:hover .cc-cell-actions {
    background: var(--cc-surface-2, #f9fafb);
  }
}

/* Progressive left-edge shadow on the pinned actions column — alpha follows the
   horizontal scroll position via --cc-actions-shadow (0 at far left → 1), so it
   fades in as content slides under the column. */
.cc-colh-actions,
.cc-cell-actions {
  box-shadow: -8px 0 10px -8px rgba(16, 24, 40, calc(0.18 * var(--cc-actions-shadow, 0)));
  transition: box-shadow 0.12s ease;
}

.cc-scroll.is-compact .cc-row--data .cc-cell {
  padding-top: 7px;
  padding-bottom: 7px;
  font-size: 12.5px;
}
.cc-scroll.is-compact .cc-row--data .cc-cell--jobTitle {
  font-size: 13px;
}
.cc-scroll.is-compact .cc-avatar {
  width: 20px;
  height: 20px;
  font-size: 8.5px;
}

/* Cell content inherits the cell size; the cell is now flex, so text spans own
   their own truncation (ellipsis) rather than the cell. */
.cc-cell-txt {
  font-size: inherit;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc-updated {
  font-size: inherit;
  color: var(--cc-text-2, #525c6b);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.is-recent {
    color: var(--cc-ok-fg, #1f7a53);
    font-weight: 600;
  }
}

.cc-refreshing {
  display: flex;
  justify-content: center;
  padding: 10px;
}

/* ===== EMPTY STATE ===== */
.cc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 60px 20px;
  text-align: center;

  .cc-empty-ic {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: var(--cc-todo-bg, #f1f3f6);
    margin-bottom: 4px;

    .v-icon {
      color: var(--cc-text-3, #8a93a3);
    }
  }

  h3 {
    font-size: 16px;
    font-weight: 700;
    margin: 0;
  }

  p {
    font-size: 13.5px;
    color: var(--cc-text-3, #888888);
    margin: 0 0 10px;
  }
}

/* ===== FOOTER / PAGINATION (prototype lvl-cc-foot / lvl-pager) ===== */
.cc-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 2px;
  flex-shrink: 0;
  border-top: 1px solid var(--cc-border, #e5e7eb);
  font-size: 12.5px;
  color: var(--cc-text-2, #525c6b);
}

.cc-foot-showing strong {
  color: var(--cc-text, #1f242d);
  font-weight: 700;
}

.cc-pager {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.cc-pgsize {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--cc-text-3, #97a0af);
  font-size: 12px;

  select {
    font: inherit;
    font-size: 12px;
    color: var(--cc-text, #1f242d);
    background: var(--cc-surface, #ffffff);
    border: 1px solid var(--cc-border, #e5e7eb);
    border-radius: 6px;
    padding: 3px 6px;
    cursor: pointer;
  }
}

.cc-pgbtn {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-surface, #ffffff);
  color: var(--cc-text-2, #525c6b);
  border-radius: 6px;
  padding: 3px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--cc-surface-2, #f7f9fb);
    color: var(--cc-text, #1f242d);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}

.cc-pgnum {
  font-size: 12px;
  color: var(--cc-text-2, #525c6b);
  font-weight: 600;
  white-space: nowrap;
}

/* ---- anchor state in the page header (latest prototype) --------------------
   Ported from .lvl-anchor-headnote / .lvl-anchor-cta-wrap in lvl/lvl.css. The pill
   stays NEUTRAL in every state with only the icon carrying colour: a draft anchor is
   work, not a breach, and amber read as "something is wrong" — it also keeps the
   12.5px label clear of contrast problems. */
.lvl-anchor-headnote {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 14px;
  padding: 3px 10px 3px 8px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--cc-text-2, #525c6b);
  white-space: nowrap;
  cursor: help;

  .v-icon { color: var(--cc-text-3, #97a0af); }

  &.is-warn,
  &.is-done { color: var(--cc-text, #1f242d); font-weight: 650; }
  &.is-warn .v-icon { color: var(--cc-accent, #1d5bd6); }
  &.is-done .v-icon { color: var(--cc-ok-fg, #1f7a53); }
  &.is-done { border-color: color-mix(in srgb, var(--cc-ok-fg, #1f7a53) 32%, transparent); }
}
/* Below this the header has no room for it, and the wizard carries the same state. */
@media (max-width: 900px) {
  .lvl-anchor-headnote { display: none; }
}

.lvl-anchor-cta-wrap { display: inline-flex; }

/* Ported from the prototype's .v3-pagehead-edit — a quiet bordered button rather than a
   Vuetify one: it is chrome for the page, not an action on the register, and the two
   should not read as equals. */
.lvl-pagehead-tut {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  padding: 7px 13px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 8px;
  background: var(--cc-surface, #fff);
  color: var(--cc-text, #1f242d);
  font: inherit;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
  transition: background 120ms, border-color 120ms;
}
.lvl-pagehead-tut .v-icon { color: var(--cc-text-2, #525c6b); }
.lvl-pagehead-tut:hover {
  background: var(--cc-canvas, #f6f7f9);
  border-color: var(--cc-border-strong, #d3d8e0);
}
</style>

<!-- Vuetify moves tooltip content to the [data-app] root, outside this component's
     scoped tree, so it cannot be reached from the scoped block above. Mirrors the
     design's .rm-tip (and framework.vue's .fw-tip): a white card rather than
     Vuetify's dark chip. -->
<style lang="scss">
.v-tooltip__content.cc-tip {
  max-width: 320px;
  padding: 9px 12px;
  background: var(--cc-surface, #ffffff) !important;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 10px;
  box-shadow: var(--cc-shadow-lg, 0 12px 32px rgba(16, 24, 40, 0.14));
  color: var(--cc-text-2, #525c6b);
  font-family: inherit;
  font-weight: 400;
  font-size: 12.5px;
  line-height: 1.5;
  letter-spacing: 0;
  text-wrap: pretty;
  /* Vuetify fades its chip to .9; the design's card is fully opaque. */
  opacity: 1 !important;
}
</style>
